/**
 * Buy it now: creates a separate Storefront API cart with only the selected
 * variant + quantity and sends the shopper to its checkout.
 * The shopper's regular cart is never read, changed or cleared.
 *
 * Uses a delegated listener on `document`, so it works for buttons that are
 * rendered later (variant changes, quick-add modal, section re-renders).
 */
(() => {
  if (window.__buyNowInit) return;
  window.__buyNowInit = true;

  const API_VERSION = '2026-07';

  const MUTATION = [
    'mutation buyNow($input: CartInput!, $country: CountryCode, $language: LanguageCode!)',
    '  @inContext(country: $country, language: $language) {',
    '  cartCreate(input: $input) {',
    '    cart { checkoutUrl }',
    '    userErrors { message }',
    '  }',
    '}',
  ].join('\n');

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-buy-now]');
    if (!button || button.disabled) return;

    const container = button.closest('.buy-now-block');
    const errorEl = container?.querySelector('[data-buy-now-error]');
    const form = button.closest('form');

    const showError = (message) => {
      if (!errorEl) return;
      errorEl.textContent = message;
      errorEl.hidden = false;
    };

    if (errorEl) errorEl.hidden = true;

    const token = button.dataset.token;
    if (!token) {
      showError('Buy it now is not configured (missing Storefront API token).');
      return;
    }

    const variantId = form?.querySelector('[name="id"]')?.value;
    const quantity = parseInt(form?.querySelector('[name="quantity"]')?.value, 10) || 1;

    if (!variantId) {
      showError('Please select a variant.');
      return;
    }

    const input = {
      lines: [
        {
          merchandiseId: `gid://shopify/ProductVariant/${variantId}`,
          quantity,
        },
      ],
    };

    if (button.dataset.country) {
      input.buyerIdentity = { countryCode: button.dataset.country };
    }

    const rawLanguage = button.dataset.language || document.documentElement.lang || 'en';
    const languageCode = rawLanguage.trim().replace(/-/g, '_').toUpperCase() || 'EN';

    const variables = { input, language: languageCode };
    if (button.dataset.country) variables.country = button.dataset.country;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(`/api/${API_VERSION}/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token,
        },
        body: JSON.stringify({ query: MUTATION, variables }),
      });

      const json = await response.json();
      const result = json?.data?.cartCreate;
      const checkoutUrl = result?.cart?.checkoutUrl;

      if (!checkoutUrl) {
        throw new Error(result?.userErrors?.[0]?.message || 'Could not start checkout.');
      }

      window.location.href = checkoutUrl;
    } catch (error) {
      console.error('[buy-now]', error);
      showError(error.message || 'Something went wrong. Please try again.');
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  });
})();